/**
 * Sign-in for the portal.
 *
 * One thing here is not cosmetic: if the portal has no client id, sign-in
 * CANNOT work, and this screen says so instead of letting every patient try
 * their password against a lookup that will never find them.
 *
 * The portal's users are invited into a Medplum project, and Medplum finds a
 * project-scoped user only when the login names that project's client. Without
 * it the server answers `400 "User not found"` — which `signIn` reports, quite
 * correctly, as "Email or password is incorrect". That message is right for a
 * wrong password and badly wrong for a misconfigured deployment, and the
 * patient has no way to tell the difference. So the check happens before the
 * form, once, where it can be stated plainly.
 */

import { useState, type FormEvent, type JSX } from 'react';

import { AuthError, signIn, type Session } from '../lib/auth.js';
import { getConfig } from '../lib/config.js';

export interface LoginScreenProps {
  readonly onSignedIn: (session: Session) => void;
}

export const LoginScreen = ({ onSignedIn }: LoginScreenProps): JSX.Element => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const configured = getConfig().medplumClientId !== null;

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    void signIn(email, password)
      .then(onSignedIn)
      .catch((caught: unknown) =>
        setError(
          caught instanceof AuthError
            ? caught.message
            : 'Could not reach the sign-in service. Check your connection and try again.',
        ),
      )
      .finally(() => setSubmitting(false));
  };

  return (
    <main className="login" aria-labelledby="login-heading">
      <h1 id="login-heading">My health</h1>
      <p className="login__profile">micro-dev · synthetic data only</p>

      {!configured ? (
        <p role="alert" className="error" data-testid="login-unconfigured">
          This portal is not configured yet, so nobody can sign in. It is missing the identifier it
          needs to find your account. This is a problem with the service, not with your password —
          please tell whoever runs it.
        </p>
      ) : (
        <form onSubmit={submit}>
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            value={email}
            autoComplete="username"
            required
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
          />

          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            value={password}
            autoComplete="current-password"
            required
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
          />

          {/* role="alert" so a screen reader announces the failure rather than
              leaving the user wondering whether the button worked. */}
          {error !== null && (
            <p role="alert" className="error" data-testid="login-error">
              {error}
            </p>
          )}

          <button type="submit" disabled={submitting} data-testid="login-submit">
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      )}

      <p className="notice" role="note">
        This environment holds <strong>synthetic data only</strong>. It must never be used with real
        patient information.
      </p>
    </main>
  );
};
